import { useEffect, useId, useState } from 'react';
import { DateInput } from '../../dateRangePicker/DateRangePicker';
import { formatDateTimeInTimezone, parseDateTimeInTimezone } from '../../../utils/date-range';
import { previewRange } from './previewDefaults';
import type { GraphTimeWindow } from './elements/types';

// The preview picker lives in a modal <dialog>: keep the calendar in its top
// layer and inside the viewport.
const PREVIEW_POPOVER_PROPS = {
    appendTo: (reference: Element) => reference.closest('dialog') ?? document.body,
    maxWidth: 'none',
    popperOptions: {
        strategy: 'fixed' as const,
        modifiers: [
            {
                name: 'preventOverflow',
                options: { altAxis: true, tether: false, padding: 8 },
            },
        ],
    },
};
const PREVIEW_POPOVER_CLASS_NAME =
    'max-h-[calc(100dvh-4rem)] w-[332px] max-w-[calc(100vw-3rem)] overflow-y-auto [&_.rdrCalendarWrapper]:w-full [&_.rdrMonth]:w-full';

export function formatPreviewDate(timestamp: number, timezone: string): string {
    return formatDateTimeInTimezone(new Date(timestamp), timezone);
}

export function parsePreviewDates(
    start: string,
    end: string,
    timezone: string
): GraphTimeWindow | null {
    const startDate = parseDateTimeInTimezone(start, timezone);
    const endDate = parseDateTimeInTimezone(end, timezone);
    return startDate && endDate
        ? previewRange(startDate.getTime(), endDate.getTime(), timezone)
        : null;
}

interface PreviewDateRangePickerProps {
    range: GraphTimeWindow;
    timezone: string;
    onChange: (range: GraphTimeWindow) => void;
}

export function PreviewDateRangePicker({
    range,
    timezone,
    onChange,
}: PreviewDateRangePickerProps): JSX.Element {
    const rangeId = useId();
    const startId = `${rangeId}-start`;
    const endId = `${rangeId}-end`;
    const [start, setStart] = useState(() => formatPreviewDate(range.viewStartTs, timezone));
    const [end, setEnd] = useState(() => formatPreviewDate(range.viewEndTs, timezone));
    const [startInputValid, setStartInputValid] = useState(true);
    const [endInputValid, setEndInputValid] = useState(true);
    useEffect(() => {
        setStart(formatPreviewDate(range.viewStartTs, timezone));
        setEnd(formatPreviewDate(range.viewEndTs, timezone));
        setStartInputValid(true);
        setEndInputValid(true);
    }, [range.viewStartTs, range.viewEndTs, timezone]);

    function changeRange(nextStart: string, nextEnd: string, otherInputValid: boolean) {
        setStart(nextStart);
        setEnd(nextEnd);
        const nextRange = parsePreviewDates(nextStart, nextEnd, timezone);
        if (nextRange && otherInputValid) onChange(nextRange);
    }

    const datesValid =
        startInputValid &&
        endInputValid &&
        parseDateTimeInTimezone(start, timezone) !== null &&
        parseDateTimeInTimezone(end, timezone) !== null;
    let error: string | null = null;
    if (!datesValid) {
        error = gettext('INVALID');
    } else if (parsePreviewDates(start, end, timezone) === null) {
        error = gettext('START_BEFORE_END_TIME');
    }
    const fields = [
        {
            id: startId,
            label: gettext('START_TIME'),
            value: start,
            onValidityChange: setStartInputValid,
            onChange: (next: string) => changeRange(next, end, endInputValid),
        },
        {
            id: endId,
            label: gettext('END_TIME'),
            value: end,
            onValidityChange: setEndInputValid,
            onChange: (next: string) => changeRange(start, next, startInputValid),
        },
    ];
    return (
        <div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {fields.map((field) => (
                    <div key={field.id} className="min-w-0">
                        <label htmlFor={field.id} className="mb-1 block text-sm text-gray-700">
                            {field.label}
                        </label>
                        <DateInput
                            id={field.id}
                            value={field.value}
                            timezone={timezone}
                            onChange={(next) => {
                                field.onChange(next);
                                return next;
                            }}
                            onValidityChange={field.onValidityChange}
                            popoverProps={PREVIEW_POPOVER_PROPS}
                            popoverClassName={PREVIEW_POPOVER_CLASS_NAME}
                        />
                    </div>
                ))}
            </div>
            {error && (
                <p role="alert" className="text-error mt-2 text-sm">
                    {error}
                </p>
            )}
        </div>
    );
}
